import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WorldPageHeader } from '../../components/world/WorldPageHeader';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { ClassSelector } from '../../components/progression/evolution/ClassSelector';
import { StatBasics } from '../../components/classes/StatBasics';
import { ClassBuildPanel } from '../../components/classes/ClassBuildPanel';
import { WhatToImprove } from '../../components/classes/WhatToImprove';
import { SourceTag } from '../../components/classes/SourceTag';
import { SkillLinkAudit } from '../../components/classes/SkillLinkAudit';
import { isStaffView } from '../../utils/staffMode';
import { getClass } from '../../data/guide/classes';
import { getClassBuild } from '../../data/guide/classBuilds';
import { missingForExact, OFFICIAL_NOTE } from '../../data/guide/statGuide';
import { useWorld } from '../../contexts/WorldDataContext';
import { useHashScroll } from '../../hooks/useHashScroll';
import type { ClassId } from '../../types/progression';
export function ClassesGuide() {
  const [params, setParams] = useSearchParams();
  const cls = getClass(params.get('clase'));
  const guide = getClassBuild(cls?.id);
  const styleId = params.get('estilo') ?? guide?.styles[0].id ?? '';
  const {
    status,
    data
  } = useWorld();
  const loading = status === 'idle' || status === 'loading';
  useHashScroll();
  useEffect(() => {
    document.title = `Clases y distribución de puntos${cls ? ` · ${cls.base}` : ''} · Guía MU Vanguard`;
  }, [cls]);
  const chooseClass = (id: ClassId) => setParams({
    clase: id
  }, {
    replace: true
  });
  const chooseStyle = (id: string) => cls && setParams({
    clase: cls.id,
    estilo: id
  }, {
    replace: true
  });
  return <div className="space-y-12 md:space-y-16" data-id="element-2340" data-mp="9pkcgrr2ikef">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Clases y distribución de puntos'
    }]} eyebrow="Guía de personajes" title="Clases y distribución de puntos" data-id="element-2341" data-mp="no1waedurcr2">
        <p className="mt-2 max-w-2xl text-sm text-gray-300 md:text-base" data-id="element-2342" data-mp="qq6fgmb3hgxg">
          Qué hace cada clase, qué estadísticas necesita, qué estilos puedes elegir y cómo avanzar sin repartir tus puntos a ciegas. Las distribuciones son orientaciones para empezar, no builds
          óptimas comprobadas en Vanguard.
        </p>
        <div className="mt-3" data-id="element-2343" data-mp="15fklbkfgpg83">
          <CopyLinkButton data-id="element-2344" data-mp="15glzvyvoskuk" />
        </div>
      </WorldPageHeader>

      <StatBasics data-id="element-2345" data-mp="19h5xt4o33shr" />

      <div className="space-y-8" data-id="element-2346" data-mp="19fmj3ii2tpi3">
        <ClassSelector value={cls?.id} onChange={chooseClass} data-id="element-2347" data-mp="19iqqeoqu91cz" />
        {cls && guide ? <ClassBuildPanel key={cls.id} cls={cls} guide={guide} styleId={styleId} onStyle={chooseStyle} w={data} loading={loading} data-id="element-2348" data-mp="1edwenu7pqs48" /> : <p className="text-sm text-gray-400" data-id="element-2349" data-mp="1oegh306yy2o1">Elige tu clase para ver sus estilos, prioridades, habilidades y equipo.</p>}
      </div>

      <WhatToImprove key={cls?.id ?? 'general'} cls={cls} guide={guide} w={data} data-id="element-2350" data-mp="3l81hzwv0oxzj" />

      {isStaffView && data && <SkillLinkAudit w={data} data-id="element-2351" data-mp="3q5jqxn91bd7j" />}

      <section id="fuentes" aria-labelledby="fuentes-t" className="grid gap-8 border-t border-white/10 pt-8 md:grid-cols-2" data-id="element-2352" data-mp="279x7wicr8b0v">
        <div data-id="element-2353" data-mp="22ie2emzvkgx0">
          <h2 id="fuentes-t" className="font-cinzel text-lg font-bold text-white" data-id="element-2354" data-mp="22inldqmi5nt7">
            De dónde sale cada dato
          </h2>
          <dl className="mt-3 space-y-2 text-sm" data-id="element-2355" data-mp="1yfheqhvh9um7">
            {([['oficial', 'Descripción de la clase y mecánicas generales, según las guías de Webzen.'], ['orientacion', 'Las prioridades de cada estilo: recomendaciones generales de esta guía.'], ['config', 'Clases activas, equipo compatible, ítems que enseñan poderes y sus requisitos de nivel y energía.'], ['probado', 'Ningún dato de esta sección tiene todavía una prueba documentada dentro del juego.']] as const).map(([k, t]) => <div key={k} data-id="element-2356" data-mp="15qtpkb7lyl3d">
                <dt data-id="element-2357" data-mp="10tk416ncatkt">
                  <SourceTag kind={k} data-id="element-2358" data-mp="10t1vbajhqmw0" />
                </dt>
                <dd className="text-gray-300" data-id="element-2359" data-mp="2oqt1c4kpdbaf">{t}</dd>
              </div>)}
          </dl>
          <p className="mt-3 text-xs text-gray-500" data-id="element-2360" data-mp="2thhtlm47icam">{OFFICIAL_NOTE}</p>
        </div>
        <div data-id="element-2361" data-mp="2tjcex1je46f8">
          <h2 className="font-cinzel text-lg font-bold text-white" data-id="element-2362" data-mp="2fqcz0yfp4a75">Qué falta para recomendar cantidades exactas</h2>
          <ul className="mt-3 space-y-2" data-id="element-2363" data-mp="2koo99a9wkmpw">
            {missingForExact.map(m => <li key={m} className="flex gap-2 text-sm text-gray-300" data-id="element-2364" data-mp="38f1scbaifb8c">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-500" aria-hidden="true" data-id="element-2365" data-mp="38co1kemv4l6l" />
                {m}
              </li>)}
          </ul>
        </div>
      </section>
    </div>;
}